<script setup>
import { computed, inject } from 'vue';
import { marked } from 'marked';
import { AlertCircle } from '@lucide/vue';
import BrandIcon from '../BrandIcon.vue';
import { useTranslations } from '../../composables/useTranslations.js';

const props = defineProps({
    role: { type: String, required: true },
    content: { type: String, default: '' },
    streaming: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    errorKey: { type: String, default: '' },
});

const config = inject('aia.config', null);
const { t } = useTranslations(config?.locale);

marked.setOptions({ breaks: true, gfm: true });

const html = computed(() => {
    if (props.role !== 'assistant') return '';
    try {
        return marked.parse(props.content || '');
    } catch (_) {
        return props.content;
    }
});

// Localized error line shown inside an errored assistant bubble.
const errorText = computed(() => t(`states.${props.errorKey || 'error'}`));
</script>

<template>
    <div
        class="aia-bubble-enter flex w-full gap-3"
        :class="role === 'user' ? 'justify-end' : 'justify-start'"
    >
        <BrandIcon v-if="role === 'assistant'" :size="30" :stroke-width="2.25" />
        <div
            class="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed"
            :class="[
                role === 'user'
                    ? 'bg-[var(--accent)] text-[var(--accent-foreground)] rounded-tr-md'
                    : 'bg-[var(--card)] text-[var(--card-foreground)] rounded-tl-md',
                error ? 'border border-[var(--destructive)]' : '',
            ]"
        >
            <!-- Assistant: markdown-rendered. User: plain text. -->
            <div
                v-if="role === 'assistant' && content"
                class="prose-aia"
                v-html="html"
            />
            <span v-else-if="role !== 'assistant'" class="whitespace-pre-wrap break-words">{{ content }}</span>
            <span
                v-if="streaming && role === 'assistant'"
                class="ml-0.5 inline-block h-3 w-1.5 align-middle bg-[var(--primary)] animate-pulse"
                aria-hidden="true"
            />

            <!-- Inline error line: shown when this assistant turn failed.
                 Adds explanatory text + icon instead of leaving an empty,
                 red-bordered bubble. -->
            <div
                v-if="error && role === 'assistant'"
                class="flex items-center gap-2 text-[var(--destructive)]"
                :class="content ? 'mt-2 pt-2 border-t border-[var(--destructive)]/30' : ''"
            >
                <AlertCircle :size="14" class="shrink-0" />
                <span>{{ errorText }}</span>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Entrance animation — every freshly-mounted bubble fades up from below.
 * The placeholder assistant turn (still streaming) animates once at the
 * moment it's appended; subsequent token-by-token updates don't re-run it. */
.aia-bubble-enter {
    animation: aia-bubble-in 280ms cubic-bezier(0.2, 0.65, 0.25, 1) backwards;
}
@keyframes aia-bubble-in {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
    .aia-bubble-enter { animation: none; }
}

.prose-aia :deep(p) { margin: 0 0 0.5rem 0; }
.prose-aia :deep(p:last-child) { margin-bottom: 0; }
.prose-aia :deep(ul), .prose-aia :deep(ol) { margin: 0.25rem 0 0.5rem 1.25rem; }
.prose-aia :deep(li) { margin: 0.125rem 0; }
.prose-aia :deep(strong) { font-weight: 600; }
.prose-aia :deep(a) { color: var(--primary); text-decoration: underline; text-underline-offset: 2px; }
.prose-aia :deep(a:hover) { opacity: 0.85; }
.prose-aia :deep(code) {
    background: rgba(255, 255, 255, 0.08);
    padding: 1px 4px;
    border-radius: 4px;
    font-size: 0.9em;
}
</style>
